import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { AppDispatch, RootState } from 'store'
import { checkIn, getBedsHistory } from 'features/accommodation/accommodationSlice'
import { IConfirmCheckInModal } from '../ConfirmCheckInModal'
import {
  IAssignBedModalEmployee,
  IAssignBedModalGuest,
  IAssignBedTarget,
} from 'types/accommodation.interface'
import { ISelectValue } from 'constants/globalTypes'
import { CloseIcon } from 'assets/icons/CloseIcon'
import { Modal } from 'components/Modal/Modal'
import { Input } from 'components/Input/Input'
import { HonestSelectInput } from 'components/HonestSelect/HonestSelectInput'
import { HonestSelectAsyncInput } from 'components/HonestAsyncSelect/HonestAsyncSelectInput'
import { Button } from 'components/ui/Button/Button'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { CursorPointer, Flex } from 'styling/GlobalStyles'
import { BodyText, OtherText, TitleText } from 'styling/Typography'
import { formatDateForDisplay } from 'utils/helpers'
import useModalClose from 'hooks/useModalClose'
import { Container, ContainerContent, ContainerHeader, HeaderButton } from '../Accommodation.style'
import {
  FieldRow,
  FieldStack,
  HeadLocation,
  HeadMeta,
  ModalHead,
  WindowBanner,
  WindowLabel,
  WindowValue,
} from './AssignBedModal.styled'

const genderOptions = [
  { value: 1, label: 'Male' },
  { value: 2, label: 'Female' },
  { value: 3, label: 'Both' },
]

const tierOptions = [
  { value: 1, label: 'VIP' },
  { value: 2, label: 'Senior' },
  { value: 3, label: 'Junior' },
  { value: 4, label: 'Contractor' },
]

const emptyEmployee: IAssignBedModalEmployee = {
  label: '',
  value: null,
  role: null,
  gender: null,
  id: null,
  tierPolicy: null,
  profileImageUrl: '',
}

const emptyGuest: IAssignBedModalGuest = {
  firstName: '',
  lastName: '',
  gender: null,
  tierPolicy: null,
  identificationNumber: null,
}

interface IAssignBedModal {
  closeModal: (assigned?: boolean) => void
  target: IAssignBedTarget
  from: Date
  to: Date
}

export const AssignBedModal = ({ closeModal, target, from, to }: IAssignBedModal) => {
  const dispatch = useDispatch<AppDispatch>()
  const { bedsHistory } = useSelector((state: RootState) => state.accomodation)
  useModalClose(closeModal)

  const [type, setType] = useState('Employee')
  const [selectedEmployee, setSelectedEmployee] = useState<IAssignBedModalEmployee>(emptyEmployee)
  const [guest, setGuest] = useState<IAssignBedModalGuest>(emptyGuest)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    dispatch(getBedsHistory({ page: 1, limit: 999 }))
  }, [dispatch])

  const openStaysByUser = useMemo(
    () =>
      (bedsHistory?.data || []).reduce(
        (acc, o) => {
          if (!o.actualCheckOutTimestamp && o.personType === 1 && o.user?.id) {
            ;(acc[o.user.id] = acc[o.user.id] || []).push({
              checkIn: o.checkInTimestamp,
              checkOut: o.expectedCheckOutTimestamp,
              location: `${o.bed?.label} · ${o.roomName} · ${o.campName}`,
            })
          }
          return acc
        },
        {} as { [userId: number]: { checkIn: string; checkOut: string; location: string }[] },
      ),
    [bedsHistory],
  )

  const handleGuestChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name } = e.target
    setGuest((prev) => ({ ...prev, [name]: value }))
  }

  const handleGuestSelect = (v: ISelectValue, name: string) => {
    setGuest((prev) => ({ ...prev, [name]: v }))
  }

  const disabled =
    type === 'Employee'
      ? !selectedEmployee.value
      : !guest.firstName || !guest.lastName || !guest.gender?.value || !guest.tierPolicy?.value

  const handleAssign = async () => {
    if (disabled) return

    if (type === 'Employee' && selectedEmployee.value) {
      const newIn = new Date(from).getTime()
      const newOut = new Date(to).getTime()
      const clash = (openStaysByUser[selectedEmployee.value] || []).find(
        (s) => newIn < new Date(s.checkOut).getTime() && new Date(s.checkIn).getTime() < newOut,
      )
      if (clash) {
        toast.error(
          `${selectedEmployee.label} already has a booking over these dates — ${clash.location}. Pick non-overlapping dates or check them out first.`,
        )
        return
      }
    }

    const person =
      type === 'Employee' ? selectedEmployee.label : `${guest.firstName} ${guest.lastName}`

    const obj: IConfirmCheckInModal = {
      person,
      type,
      checkIn: new Date(from).toISOString(),
      checkOut: new Date(to).toISOString(),
      bed: target.label,
      bedId: target.bedId,
      userId: type === 'Employee' ? selectedEmployee.value : null,
      displayId:
        type === 'Employee'
          ? selectedEmployee.id
            ? String(selectedEmployee.id)
            : '/'
          : 'New guest',
      gender:
        type === 'Employee'
          ? (selectedEmployee.gender as unknown as ISelectValue)
          : guest.gender,
      identificationNumber: type === 'Guest' ? guest.identificationNumber : null,
      location: target.location,
    }

    setSaving(true)
    const resp = await dispatch(checkIn(obj))
    setSaving(false)
    if (resp.meta.requestStatus === 'fulfilled') {
      toast.success(`Assigned ${person} to ${target.label}`)
      closeModal(true)
    }
  }

  return (
    <Modal black>
      <Flex column width='100%'>
        <ModalHead>
          <HeadMeta>
            <TitleText weight='bold'>Assign — {target.label}</TitleText>
            <HeadLocation>{target.location}</HeadLocation>
          </HeadMeta>
          <CursorPointer onClick={() => closeModal()}>
            <CloseIcon />
          </CursorPointer>
        </ModalHead>

        <WindowBanner>
          <WindowLabel>Window</WindowLabel>
          <WindowValue>
            {formatDateForDisplay(from)} → {formatDateForDisplay(to)}
          </WindowValue>
        </WindowBanner>

        <Container>
          <ContainerHeader>
            <HeaderButton active={type === 'Employee'} onClick={() => setType('Employee')}>
              Employee
            </HeaderButton>
            <HeaderButton active={type === 'Guest'} onClick={() => setType('Guest')}>
              Guest
            </HeaderButton>
          </ContainerHeader>
          <ContainerContent style={{ gap: '1rem' }}>
            {type === 'Employee' ? (
              <>
                <TitleText style={{ fontSize: 18 }}>Search Employee</TitleText>
                <BodyText grey>Search from HRDL system</BodyText>
                <HonestSelectAsyncInput
                  nomarg
                  link='users/basic'
                  name='employee'
                  onChange={(v) => setSelectedEmployee({ ...v })}
                  defaultValue={selectedEmployee?.label}
                  additionalParams={{ hasAccommodationProvided: true }}
                  parserFunction={(data: any) =>
                    data.data.map((i: any) => ({
                      label: i.fullName,
                      profilePictureUrl: i.getImagePresignedUrl,
                      value: i.id,
                      id: i.accessCardId,
                      role: i.role,
                      gender: i.gender,
                      tierPolicy: i.currentContract?.logisticsInfo?.accomodationTierPolicy,
                    }))
                  }
                />
                {selectedEmployee?.value ? (
                  <Flex center gap='0.5rem'>
                    <StatusPill status='info'>Employee</StatusPill>
                    <OtherText grey>ID: {selectedEmployee?.id || '/'}</OtherText>
                  </Flex>
                ) : null}
              </>
            ) : (
              <>
                <TitleText style={{ fontSize: 18 }}>Guest Registration</TitleText>
                <BodyText grey>Manual entry for non-employee guests</BodyText>
                <FieldStack>
                  <FieldRow>
                    <Input
                      onChange={handleGuestChange}
                      value={guest.firstName}
                      name='firstName'
                      labelText='First Name'
                      nomarg
                      placeholder='Enter here...'
                    />
                    <Input
                      onChange={handleGuestChange}
                      value={guest.lastName}
                      name='lastName'
                      labelText='Last Name'
                      nomarg
                      placeholder='Enter here...'
                    />
                  </FieldRow>
                  <FieldRow>
                    <HonestSelectInput
                      options={genderOptions}
                      labelText='Gender'
                      defaultValue={guest?.gender?.value}
                      name='gender'
                      onChange={handleGuestSelect}
                      nomarg
                    />
                    <HonestSelectInput
                      onChange={handleGuestSelect}
                      options={tierOptions}
                      defaultValue={guest?.tierPolicy?.value}
                      labelText='Tier'
                      name='tierPolicy'
                      nomarg
                    />
                  </FieldRow>
                  <Input
                    onChange={handleGuestChange}
                    value={guest.identificationNumber}
                    name='identificationNumber'
                    labelText='Identification Number'
                    nomarg
                    placeholder='Enter here...'
                  />
                </FieldStack>
              </>
            )}
          </ContainerContent>
        </Container>

        <Button
          variant='primary'
          style={{ marginTop: '1rem', width: '100%' }}
          disabled={disabled || saving}
          onClick={handleAssign}
        >
          Assign to bed
        </Button>
      </Flex>
    </Modal>
  )
}
